import { apiConstants, processServerResponse } from '../../util/apiUtils';
import * as assert from 'assert';

interface IRequestConfig {
    method: string;
    headers: Headers;
    body?: string | FormData;
}

export abstract class ApiService {
    protected abstract basePath: string;
    protected authToken: string;

    constructor(authToken: string) {
        this.authToken = authToken;
    }

    protected postRequest(data?: object | FormData) {
        return this.authenticatedRequest(apiConstants.methods.POST, this.basePath, data);
    }

    protected putRequest(data?: object | FormData) {
        return this.authenticatedRequest(apiConstants.methods.PUT, this.basePath, data);
    }

    protected getRequest(data?: object) {
        return this.authenticatedRequest(apiConstants.methods.GET, this.basePath, data);
    }

    protected getSubPath(subPath: string, data?: object) {
        const path: string = this.basePath + '/' + subPath;
        return this.authenticatedRequest(apiConstants.methods.GET, path, data);
    }

    protected postSubPath(subPath: string, data?: object) {
        const path: string = this.basePath + '/' + subPath;
        return this.authenticatedRequest(apiConstants.methods.POST, path, data);
    }

    protected putSubPath(subPath: string, data?: object) {
        const path: string = this.basePath + '/' + subPath;
        return this.authenticatedRequest(apiConstants.methods.PUT, path, data);
    }

    protected deleteSubPath(subPath: string, data?: object) {
        const path: string = this.basePath + '/' + subPath;
        return this.authenticatedRequest(apiConstants.methods.DELETE, path, data);
    }

    protected authenticatedRequest(method: string, path: string, data?: object | FormData): Promise<any> {
        let url: string = apiConstants.routes.SERVER + path;
        // TODO url validation

        let requestConfig: IRequestConfig = {
            method: method,
            headers: this.getAuthHeaders()
        };

        if (method !== apiConstants.methods.GET) {
            if (data) {
                let proto = Object.getPrototypeOf(data);
                if (proto === FormData.prototype) {
                    requestConfig.body = data as FormData;
                    // Note: for file upload to work correctly, we must not specify a content type header,
                    // this forces the browser to fill in the headers required for a multipart/form-data request
                } else {
                    requestConfig.body = JSON.stringify(data);
                    requestConfig.headers.append(apiConstants.headers.ContentType, apiConstants.headers.JSON);
                }
            }
        }

        if (method === apiConstants.methods.GET) {
            if (data) {
                url += '?' + this.formatQueryParams(data);
            }
        }

        return fetch(url, requestConfig)
            .then(response => {
                return processServerResponse(response);
            })
            .then((data) => {
                assert.notEqual(
                    null,
                    data,
                    'Assertion Fail @ ApiService#authenticatedRequest: no data'
                );

                return data;
            });
    }

    private formatQueryParams(paramsObject: any) {
        const tmp: string[] = [];
        Object.keys(paramsObject).forEach(key => {
            const val: any = paramsObject[key];
            if (Array.isArray(val)) {
                val.forEach(entry => {
                    tmp.push(`${key}[]=` + encodeURIComponent(entry));
                });
            } else if (typeof val === 'string' || typeof val === 'number') {
                tmp.push(`${key}=` + encodeURIComponent(val.toString()));
            } else {
                throw new Error('invalid use of formatQueryParams, passed parameter object with unsupported structure');
            }
        });

        return tmp.join('&');
    }

    private getAuthHeaders(): Headers {
        assert.notEqual(
            null,
            this.authToken,
            'Assertion Fail @ getAuthHeaders: no authToken'
        );
        const headers: Headers = new Headers();
        headers.append(apiConstants.headers.Authorization, this.authToken);

        return headers;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/data_layer/services/ApiService.ts